import blogSeoData from './blogSeo.json';

type BlogSeoEntry = {
  seoTitle: string;
  seoDescription: string;
  category: string;
};

export type OfficialSource = {
  label: string;
  url: string;
};

export type TrustNotice = {
  title: string;
  text: string;
  sources: OfficialSource[];
};

export type ContextLink = {
  label: string;
  path: string;
};

const BLOG_SEO = blogSeoData as Record<string, BlogSeoEntry>;
const TITLE_SUFFIX = ' | Ayşegül Saraç';

export const getBlogSeo = (slug: string): BlogSeoEntry | undefined => BLOG_SEO[slug];

export const getBlogCategory = (slug: string): string =>
  BLOG_SEO[slug]?.category || "Almanya'da Yaşam";

export const getBlogSeoTitle = (slug: string, fallbackTitle: string): string => {
  const title = BLOG_SEO[slug]?.seoTitle || fallbackTitle;
  return `${title}${TITLE_SUFFIX}`.length <= 65 ? `${title}${TITLE_SUFFIX}` : title;
};

export const getBlogSeoDescription = (slug: string, fallbackDescription: string): string =>
  BLOG_SEO[slug]?.seoDescription || fallbackDescription;

const contextLinkByCategory: Record<string, ContextLink> = {
  'Almanya Blue Card': { label: 'Vize rehberliği hizmetini inceleyin', path: '/hizmetler-vize' },
  "Almanya'da Eğitim": { label: 'Eğitim rehberliği hizmetini inceleyin', path: '/hizmetler-egitim' },
  "Almanya'da Yaşam": { label: 'Göç sonrası rehberlik hizmetini inceleyin', path: '/hizmetler-goc-sonrasi' },
  "Almanya'ya Göç": { label: 'Göç süreci rehberliği hizmetlerini inceleyin', path: '/hizmetlerimiz' },
  "Ayşegül'den": { label: 'Tüm Göç Notları yazılarını inceleyin', path: '/goc-notlari' },
};

export const getBlogContextLink = (slug: string): ContextLink =>
  contextLinkByCategory[getBlogCategory(slug)] || contextLinkByCategory["Ayşegül'den"];

const officialSources = {
  visa: [
    {
      label: 'Almanya Dışişleri Bakanlığı — Visa-Navigator',
      url: 'https://www.auswaertiges-amt.de/de/2315184-2315184',
    },
    {
      label: 'Federal Hükûmet portalı — Almanya Nitelikli Göç Yasası',
      url: 'https://www.make-it-in-germany.com/de/visum-aufenthalt/fachkraefteeinwanderungsgesetz',
    },
  ],
  blueCard: [
    {
      label: 'Federal Hükûmet portalı — Blaue Karte EU',
      url: 'https://www.make-it-in-germany.com/de/visum-aufenthalt/arten/blaue-karte-eu',
    },
    {
      label: 'Almanya Dışişleri Bakanlığı — Visa-Navigator',
      url: 'https://www.auswaertiges-amt.de/de/2315184-2315184',
    },
  ],
  residence: [
    {
      label: 'Federal Hükûmet portalı — Niederlassungserlaubnis',
      url: 'https://www.make-it-in-germany.com/de/visum-aufenthalt/dauerhaft-in-deutschland/niederlassungserlaubnis',
    },
    {
      label: 'Federal Hükûmet portalı — Blaue Karte EU',
      url: 'https://www.make-it-in-germany.com/de/visum-aufenthalt/arten/blaue-karte-eu',
    },
  ],
  registration: [
    {
      label: 'Bundesportal — Wohnsitz anmelden',
      url: 'https://verwaltung.bund.de/leistungsverzeichnis/DE/leistung/99115005104000',
    },
    {
      label: 'Federal Hükûmet portalı — Wohnen und Anmelden',
      url: 'https://www.make-it-in-germany.com/de/leben-in-deutschland/wohnen-mobilitaet/wohnen-anmelden',
    },
  ],
  health: [
    {
      label: 'Federal Sağlık Bakanlığı — Hausarztsystem',
      url: 'https://www.bundesgesundheitsministerium.de/hausarztsystem/',
    },
    {
      label: 'Federal Sağlık Bakanlığı — Freie Arztwahl',
      url: 'https://www.bundesgesundheitsministerium.de/themen/krankenversicherung/grundprinzipien/freie-arztwahl',
    },
  ],
  finance: [
    {
      label: 'BaFin — Bankalar ve tüketici kredileri rehberi',
      url: 'https://www.bafin.de/SharedDocs/Downloads/DE/Broschuere/dl_b_banken_leichte_sprache.pdf?__blob=publicationFile&v=7',
    },
  ],
  business: [
    {
      label: 'Federal Ekonomi Bakanlığı — Unternehmensanmeldung',
      url: 'https://www.existenzgruendungsportal.de/Navigation/DE/So-gehts/Unternehmensanmeldung/unternehmensanmeldung',
    },
  ],
} satisfies Record<string, OfficialSource[]>;

const topicBySlug: Record<string, keyof typeof officialSources> = {
  '/post/kalici-oturum-a1-einburgerungstest': 'residence',
  '/post/almanyada-hausarzt-bulma': 'health',
  '/post/almanyada-sirket-kurmak': 'business',
  '/post/almanya-da-kredi-cekmek': 'finance',
  '/post/almanya_ezb_formu': 'residence',
  '/post/ab-vatandaslarinin-almanyaya-gocu': 'visa',
  '/post/almanyaya-nasil-goc-ederim': 'visa',
  '/post/almanyada-anmeldung-nedir-ve-nasil-yapilir': 'registration',
  '/post/almanyaya-goc-etmenin-yollari': 'visa',
  '/post/almanyaya-cocuk-ile-goc': 'visa',
  '/post/blue-card-bilgi': 'blueCard',
  '/post/almanya-blue-card-vize': 'blueCard',
  '/post/almanyaya-geldik-simdi-ne-olacak': 'registration',
  '/post/almanya-blue-card': 'blueCard',
};

const noticeTextByTopic: Record<keyof typeof officialSources, string> = {
  visa:
    'Bu yazı kişisel deneyim ve genel bilgilendirme amacı taşır; hukuki danışmanlık değildir. Vize ve göç kuralları kişisel duruma ve tarihe göre değişebilir. Başvuru öncesinde yetkili makamların güncel açıklamalarını kontrol edin.',
  blueCard:
    'Bu yazı kişisel deneyim ve genel bilgilendirme amacı taşır; hukuki danışmanlık değildir. Blue Card maaş eşikleri ve başvuru koşulları dönemsel olarak değişebilir. Güncel şartları resmî kaynaklardan doğrulayın.',
  residence:
    'Bu yazı kişisel deneyim ve genel bilgilendirme amacı taşır; hukuki danışmanlık değildir. Oturum koşulları kişisel statüye ve başvuru tarihine göre değişebilir. Güncel koşulları yetkili yabancılar dairesi ve resmî kaynaklardan doğrulayın.',
  registration:
    'Bu yazı kişisel deneyim ve genel bilgilendirme amacı taşır. Adres kaydı işlemleri belediyeye ve kişisel duruma göre farklılık gösterebilir; güncel belge ve randevu koşullarını yerel makamdan doğrulayın.',
  health:
    'Bu yazı kişisel deneyim ve genel bilgilendirme amacı taşır; tıbbi tavsiye değildir. Sağlık hizmetleri ve sigorta kapsamı kişisel duruma göre değişebilir. Tıbbi konularda yetkili sağlık kuruluşuna başvurun.',
  finance:
    'Bu yazı kişisel deneyim ve genel bilgilendirme amacı taşır; finansal danışmanlık değildir. Kredi şartları bankaya, gelire ve kişisel kredi değerlendirmesine göre değişebilir. Sözleşme öncesinde sağlayıcının güncel koşullarını inceleyin.',
  business:
    'Bu yazı kişisel deneyim ve genel bilgilendirme amacı taşır; vergi veya hukuk danışmanlığı değildir. Şirket türü ve kayıt yükümlülükleri faaliyete göre değişebilir. Güncel koşulları yetkili makam veya uzmanla doğrulayın.',
};

export const getBlogTrustNotice = (slug: string): TrustNotice | undefined => {
  const topic = topicBySlug[slug];
  if (!topic) return undefined;

  return {
    title: 'Önemli bilgilendirme',
    text: noticeTextByTopic[topic],
    sources: officialSources[topic],
  };
};

const escapeHtml = (value: string): string => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

export const normalizeBlogHeadings = (html: string, title: string): string => {
  let normalized = html
    .replace(/<h4\b/gi, '<h2')
    .replace(/<\/h4>/gi, '</h2>')
    .replace(/<h5\b/gi, '<h3')
    .replace(/<\/h5>/gi, '</h3>')
    .replace(/<h6\b/gi, '<h3')
    .replace(/<\/h6>/gi, '</h3>');

  if (!/<h2\b/i.test(normalized) && /<h3\b/i.test(normalized)) {
    normalized = normalized.replace(/<h3\b/gi, '<h2').replace(/<\/h3>/gi, '</h2>');
  }

  if (!/<h2\b/i.test(normalized)) {
    normalized = `<h2>${escapeHtml(title)} hakkında</h2>${normalized}`;
  }

  return normalized;
};
